import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import CustomSelect from "../../../../../../components/common/Select";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";
import apiService from "../../../../../../service/Api";
import { CommentCard } from "./CommentCard";
import { MemoryMessage } from "../hooks/useMemoryMessages";

interface CommentsTabProps {
  messages: MemoryMessage[];
  onCountChange?: (count: number) => void;
  onStatsChange?: () => void;
}

export default function CommentsTab({
  messages,
  onCountChange,
  onStatsChange,
}: CommentsTabProps) {
  const [localMessages, setLocalMessages] = useState<MemoryMessage[]>(messages);
  const [checkedIds, setCheckedIds] = useState<number[]>([]);
  const [filterValue, setFilterValue] = useState("all");
  const [bulkAction, setBulkAction] = useState("approve");

  useEffect(() => {
    setLocalMessages(messages);
    onCountChange?.(messages.length);
  }, [messages, onCountChange]);

  const filtered = useMemo(() => {
    switch (filterValue) {
      case "approved":
        return localMessages.filter((c) => c.is_approved);
      case "pending":
        return localMessages.filter((c) => !c.is_approved && !c.is_spam);
      case "spam":
        return localMessages.filter((c) => c.is_spam);
      default:
        return localMessages;
    }
  }, [filterValue, localMessages]);

  const toggleCheck = (id: number) => {
    setCheckedIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    );
  };

  const handleApprove = async (commentId: number, isApproved: boolean) => {
    const endpoint = isApproved
      ? `/api/chats/memorial-message/bulk-unapprove/`
      : `/api/chats/memorial-message/bulk-approve/`;
    // Optimistic update
    setLocalMessages((prev) =>
      prev.map((c) =>
        c.id === commentId ? { ...c, is_approved: !isApproved } : c,
      ),
    );
    onStatsChange?.();
    try {
      await apiService.post(endpoint, { message_ids: [commentId] });
    } catch {
      // Revert
      setLocalMessages((prev) =>
        prev.map((c) =>
          c.id === commentId ? { ...c, is_approved: isApproved } : c,
        ),
      );
      onStatsChange?.();
      showErrorToast("Failed to update comment");
    }
  };

  const handleSpam = async (commentId: number) => {
    const backup = localMessages.find((c) => c.id === commentId);
    // Optimistic update
    setLocalMessages((prev) => {
      const updated = prev.filter((c) => c.id !== commentId);
      onCountChange?.(updated.length);
      return updated;
    });
    onStatsChange?.();
    try {
      await apiService.delete(`/api/chats/memorial-message/${commentId}/`);
      showSuccessToast("Comment removed");
    } catch {
      // Revert
      if (backup) {
        setLocalMessages((prev) => {
          const restored = [...prev, backup].sort(
            (a, b) =>
              new Date(b.created_at).getTime() -
              new Date(a.created_at).getTime(),
          );
          onCountChange?.(restored.length);
          return restored;
        });
        onStatsChange?.();
      }
      showErrorToast("Failed to remove comment");
    }
  };

  const bulkActionLabel =
    bulkAction === "approve"
      ? "approved"
      : bulkAction === "unapprove"
        ? "unapproved"
        : "removed";

  const handleBulkApply = async () => {
    if (checkedIds.length === 0) {
      toast.warning("Please select comments first");
      return;
    }
    try {
      if (bulkAction === "spam") {
        await Promise.all(
          checkedIds.map((id) =>
            apiService.delete(`/api/chats/memorial-message/${id}/`),
          ),
        );
        setLocalMessages((prev) => {
          const updated = prev.filter((c) => !checkedIds.includes(c.id));
          onCountChange?.(updated.length);
          return updated;
        });
      } else {
        await apiService.post(
          `/api/chats/memorial-message/bulk-${bulkAction}/`,
          { message_ids: checkedIds },
        );
        setLocalMessages((prev) =>
          prev.map((c) =>
            checkedIds.includes(c.id)
              ? { ...c, is_approved: bulkAction === "approve" }
              : c,
          ),
        );
      }
      onStatsChange?.();
      setCheckedIds([]);
      showSuccessToast(`Comments ${bulkActionLabel} successfully`);
    } catch {
      showErrorToast("Failed to apply bulk action");
    }
  };

  return (
    <Box>
      {/* Toolbar */}
      <Stack
        direction="row"
        spacing={1.5}
        alignItems="center"
        sx={{ mb: 2, width: "fit-content" }}
      >
        <CustomSelect
          onChange={(e) => setFilterValue(String(e.target.value))}
          label=""
          defaultValue="all"
          sx={{ width: 150, borderRadius: "8px" }}
          size="small"
        >
          <MenuItem value="all">All comments</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="pending">Pending</MenuItem>
        </CustomSelect>

        <CustomSelect
          onChange={(e) => setBulkAction(String(e.target.value))}
          label="Bulk Actions"
          defaultValue="approve"
          sx={{ width: 150 }}
          size="small"
        >
          <MenuItem value="approve">Approve</MenuItem>
          <MenuItem value="spam">Spam</MenuItem>
          <MenuItem value="unapprove">Unapprove</MenuItem>
        </CustomSelect>

        <Button
          variant="contained"
          sx={{
            bgcolor: "#724e41",
            color: "white",
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 700,
            fontSize: "13px",
            height: 38,
            "&:hover": { bgcolor: "#5d3e33" },
          }}
          onClick={handleBulkApply}
        >
          Apply
        </Button>
      </Stack>

      {/* Comments List */}
      <Paper
        sx={{
          borderRadius: "16px",
          p: 3,
          bgcolor: "background.paper",
          boxShadow: 0,
          maxHeight: 520,
          overflowY: "auto",
          "&::-webkit-scrollbar": { width: 4 },
          "&::-webkit-scrollbar-track": { bgcolor: "transparent" },
          "&::-webkit-scrollbar-thumb": { bgcolor: "action.hover", borderRadius: 4 },
        }}
      >
        <Stack spacing={0}>
          {filtered.map((comment, index) => (
            <Box key={comment.id}>
              <CommentCard
                comment={comment}
                checked={checkedIds.includes(comment.id)}
                onCheck={() => toggleCheck(comment.id)}
                isLast={index === filtered.length - 1}
                onApprove={(isApproved) =>
                  handleApprove(comment.id, isApproved)
                }
                onSpam={() => handleSpam(comment.id)}
              />
            </Box>
          ))}
          {filtered.length === 0 && (
            <Box sx={{ py: 6, textAlign: "center" }}>
              <Typography sx={{ color: "text.secondary", fontSize: "13px" }}>
                No comments found
              </Typography>
            </Box>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}